package dev.akshaychordiya

import com.varabyte.kobweb.compose.css.ScrollBehavior
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.TextTransform
import com.varabyte.kobweb.compose.css.functions.clamp
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.forms.ButtonStyle
import com.varabyte.kobweb.silk.components.forms.ButtonVars
import com.varabyte.kobweb.silk.init.InitSilk
import com.varabyte.kobweb.silk.init.InitSilkContext
import com.varabyte.kobweb.silk.init.registerStyleBase
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.addVariantBase
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.theme.colors.palette.background
import com.varabyte.kobweb.silk.theme.colors.palette.color
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import org.jetbrains.compose.web.css.*

const val BODY_FONT = "Inter"
const val MONO_FONT = "JetBrains Mono"

/**
 * Headlines get their own face. Inter is an excellent body font but a neutral one, so at display sizes it reads as
 * "default". Space Grotesk has visible character - tighter apertures, a squarer bowl - which gives the page a voice
 * without costing legibility, and its technical feel suits the subject matter.
 */
const val DISPLAY_FONT = "Space Grotesk"

@InitSilk
fun initSiteStyles(ctx: InitSilkContext) {
    // Silk paints the theme background onto a Surface inside <body>, leaving the root element untouched. The browser
    // uses that root for overscroll and scrollbars, so without this a light-mode page shows a dark bar when you
    // scroll past the top. `color-scheme` makes the scrollbars themselves follow the theme too.
    //
    // Styles registered on the stylesheet are plain CSS and are not colour-mode aware, so rather than one rule with
    // a `base` block we target the `silk-light` / `silk-dark` class Silk puts on <html> itself.
    ColorMode.entries.forEach { mode ->
        val palette = if (mode.isLight) ctx.theme.palettes.light else ctx.theme.palettes.dark
        ctx.stylesheet.registerStyleBase("html.silk-${mode.name.lowercase()}") {
            Modifier
                .backgroundColor(palette.background)
                .styleModifier { property("color-scheme", if (mode.isLight) "light" else "dark") }
        }
    }

    // Smooth scrolling is nice, but honouring `prefers-reduced-motion` is not optional - it makes some people
    // motion sick.
    ctx.stylesheet.registerStyle("html") {
        cssRule(CSSMediaQuery.MediaFeature("prefers-reduced-motion", StylePropertyValue("no-preference"))) {
            Modifier.scrollBehavior(ScrollBehavior.Smooth)
        }
    }

    ctx.stylesheet.registerStyleBase("body") {
        Modifier
            .fontFamily(BODY_FONT, "-apple-system", "BlinkMacSystemFont", "Segoe UI", "Helvetica Neue", "sans-serif")
            .fontSize(17.px)
            .lineHeight(1.65)
    }

    ctx.stylesheet.registerStyleBase("code, pre") {
        Modifier.fontFamily(MONO_FONT, "SF Mono", "Menlo", "Consolas", "monospace")
    }

    // A visible, consistent focus ring. Browsers' defaults vary and some get hidden by custom backgrounds.
    ctx.stylesheet.registerStyleBase(":focus-visible") {
        Modifier
            .outline(2.px, LineStyle.Solid, ctx.theme.palettes.light.color)
            .outlineOffset(2.px)
    }

    // Headings take the display face, and tighter tracking - loose tracking on large text reads as amateurish.
    ctx.stylesheet.registerStyleBase("h1, h2, h3, h4") {
        Modifier
            .fontFamily(DISPLAY_FONT, BODY_FONT, "-apple-system", "sans-serif")
            .lineHeight(1.15)
            .letterSpacing((-0.02).cssRem)
    }
}

/** Display type for page-opening headlines. Scales fluidly so it never overflows a narrow phone. */
val HeadlineTextStyle = CssStyle.base {
    Modifier
        .fontFamily(DISPLAY_FONT, BODY_FONT, "-apple-system", "sans-serif")
        .fontSize(clamp(2.1.cssRem, 5.vw, 3.4.cssRem))
        .fontWeight(700)
        .letterSpacing((-0.045).cssRem)
        .lineHeight(1.1)
        .textAlign(TextAlign.Start)
}

/** The supporting line under a headline. */
val SubheadlineTextStyle = CssStyle.base {
    Modifier
        .fontSize(1.15.cssRem)
        .lineHeight(1.6)
        .color(colorMode.toPalette().color.toRgb().copyf(alpha = 0.75f))
}

/** Section headings used to break up long pages ("Books", "Recent talks", ...). */
val SectionTitleStyle = CssStyle.base {
    Modifier
        .fontFamily(DISPLAY_FONT, BODY_FONT, "-apple-system", "sans-serif")
        .fontSize(0.8.cssRem)
        .fontWeight(600)
        .letterSpacing(0.09.cssRem)
        .textTransform(TextTransform.Uppercase)
        .color(colorMode.toSitePalette().subdued)
}

/** Small metadata text: dates, venues, durations. */
val MetaTextStyle = CssStyle.base {
    Modifier
        .fontSize(0.85.cssRem)
        .color(colorMode.toSitePalette().subdued)
}

val CircleButtonVariant = ButtonStyle.addVariantBase {
    Modifier.padding(0.px).borderRadius(50.percent)
}

val UncoloredButtonVariant = ButtonStyle.addVariantBase {
    Modifier.setVariable(ButtonVars.BackgroundDefaultColor, Colors.Transparent)
}
